import React, { useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import PublishedInterviewsList from '@/components/interview/PublishedInterviewsList';
import { usePaginatedPublishHistory } from '@/hooks/publish/usePaginatedPublishHistory';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const PublishHistoryContent: React.FC = () => {
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const { publishedInterviews, isLoading, error, page, setPage } = usePaginatedPublishHistory(itemsPerPage);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
        <p>Feil ved lasting av publiserte intervjuer: {error instanceof Error ? error.message : 'Ukjent feil'}</p>
      </div>
    );
  }

  const hasNextPage = publishedInterviews && publishedInterviews.length === itemsPerPage;
  const hasPrevPage = page > 1;

  // Generate page numbers to show
  const generatePageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxPagesToShow = 5;
    
    if (page <= 3) {
      // Show first few pages
      for (let i = 1; i <= Math.min(maxPagesToShow, page + 2); i++) {
        pages.push(i);
      }
      if (hasNextPage) pages.push('ellipsis');
    } else {
      pages.push(1);
      pages.push('ellipsis');
      for (let i = page - 1; i <= page + 1; i++) {
        pages.push(i);
      }
      if (hasNextPage) pages.push('ellipsis');
    }
    
    return pages;
  };
  
  return (
    <>
      <PublishedInterviewsList interviews={publishedInterviews || []} />
      
      {/* Pagination controls */}
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Artikler per side:</span>
          <Select 
            value={itemsPerPage.toString()} 
            onValueChange={(value) => {
              setItemsPerPage(parseInt(value));
              setPage(1); // Reset to first page when changing items per page
            }}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => hasPrevPage && setPage(page - 1)}
                className={!hasPrevPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            
            {generatePageNumbers().map((pageNum, idx) => (
              <PaginationItem key={idx}>
                {pageNum === 'ellipsis' ? (
                  <PaginationEllipsis />
                ) : (
                  <PaginationLink
                    onClick={() => setPage(pageNum)}
                    isActive={pageNum === page}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}
            
            <PaginationItem>
              <PaginationNext 
                onClick={() => hasNextPage && setPage(page + 1)}
                className={!hasNextPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>

        <div className="text-sm text-muted-foreground">
          Side {page}
        </div>
      </div>
    </>
  );
};
